import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Truck, DollarSign, Weight, Save, RotateCcw, Loader2, Shield, Bot } from 'lucide-react';

const TRUCKS = ['Cargo Van', 'Sprinter Van', 'Box Truck', 'Semi Truck'];
const META = {
  'Cargo Van':    { color: 'border-sky-200',    pill: 'bg-sky-100 text-sky-700',       tip: 'Small parcels, expedited LTL' },
  'Sprinter Van': { color: 'border-teal-200',   pill: 'bg-teal-100 text-teal-700',     tip: 'Hot-shot / expedited LTL' },
  'Box Truck':    { color: 'border-amber-200',  pill: 'bg-amber-100 text-amber-700',   tip: "26' box, medium loads" },
  'Semi Truck':   { color: 'border-purple-200', pill: 'bg-purple-100 text-purple-700', tip: 'Full truckload' },
};

const DEFAULT_AUTO_RULES = {
  min_score: 75,
  min_rate_per_mile: 2.00,
  max_deadhead_miles: 100,
  max_daily_bookings: 5,
  allowed_states: [],
  blocked_brokers: [],
};

export default function FleetRates({ user }) {
  const [rates, setRates] = useState(null);
  const [saving, setSaving] = useState(false);
  const [autoRules, setAutoRules] = useState(null);
  const [savingRules, setSavingRules] = useState(false);
  const canEdit = user?.role === 'broker' || user?.role === 'super_admin' || (user?.role === 'broker_manager' && user?.ai_mode === 'autonomous');

  useEffect(() => {
    api.get('/org/fleet-rates').then(res => setRates(res.data.fleet_rates || {})).catch(err => toast.error(err.response?.data?.detail || 'Failed to load'));
    if (canEdit) {
      api.get('/org/autonomous-rules')
        .then(res => setAutoRules({ ...DEFAULT_AUTO_RULES, ...(res.data.rules || {}) }))
        .catch(() => setAutoRules({ ...DEFAULT_AUTO_RULES }));
    }
  }, []);

  const updRule = (field, v) => setAutoRules(p => ({ ...p, [field]: v }));
  const saveRules = () => {
    setSavingRules(true);
    const payload = {
      min_score: parseFloat(autoRules.min_score) || 0,
      min_rate_per_mile: parseFloat(autoRules.min_rate_per_mile) || 0,
      max_deadhead_miles: parseFloat(autoRules.max_deadhead_miles) || 0,
      max_daily_bookings: parseFloat(autoRules.max_daily_bookings) || 0,
      allowed_states: (typeof autoRules.allowed_states === 'string'
        ? autoRules.allowed_states.split(',')
        : autoRules.allowed_states || []).map(s => String(s).trim().toUpperCase()).filter(Boolean),
      blocked_brokers: (typeof autoRules.blocked_brokers === 'string'
        ? autoRules.blocked_brokers.split(',')
        : autoRules.blocked_brokers || []).map(s => String(s).trim()).filter(Boolean),
    };
    api.put('/org/autonomous-rules', payload)
      .then(r => { setAutoRules({ ...DEFAULT_AUTO_RULES, ...r.data.rules }); toast.success('AI rules saved'); })
      .catch(e => toast.error(e.response?.data?.detail || 'Save failed'))
      .finally(() => setSavingRules(false));
  };

  const upd = (tt, field, v) => setRates(p => ({ ...p, [tt]: { ...(p[tt] || {}), [field]: v } }));
  const save = () => { setSaving(true); api.put('/org/fleet-rates', { fleet_rates: rates }).then(r => { setRates(r.data.fleet_rates); toast.success('Fleet rates saved'); }).catch(e => toast.error(e.response?.data?.detail || 'Save failed')).finally(() => setSaving(false)); };
  const restore = () => { if (window.confirm('Restore defaults?')) setRates({ 'Cargo Van':{min_rpm:1.80,weight_lbs:3000,notes:'Small loads'},'Sprinter Van':{min_rpm:2.00,weight_lbs:4000,notes:'LTL'},'Box Truck':{min_rpm:2.50,weight_lbs:12000,notes:"26' box"},'Semi Truck':{min_rpm:2.20,weight_lbs:44000,notes:'FTL'} }); };

  if (!canEdit) return <div className="text-center py-16"><Truck className="w-16 h-16 mx-auto text-slate-300 mb-4" /><h2 className="text-2xl font-bold">Broker Access Required</h2></div>;
  if (!rates) return <div className="py-16 text-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin inline mr-2" />Loading...</div>;

  return (
    <div className="space-y-6" data-testid="fleet-rates-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-purple-600 flex items-center justify-center"><Truck className="w-5 h-5 text-white" /></div>Fleet Rate Settings</h1>
          <p className="text-slate-600 mt-1 max-w-2xl">Set minimum $/mile + weight capacity per truck type. Phase 3 AI uses these based on the driver's truck type.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={restore}><RotateCcw className="w-4 h-4 mr-1" /> Restore Defaults</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-fleet-rates-btn">{saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save</>}</Button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TRUCKS.map(tt => {
          const cfg = rates[tt] || {}; const m = META[tt];
          return (
            <Card key={tt} className={`border-2 ${m.color}`} data-testid={`fleet-card-${tt.replace(/\s+/g,'-').toLowerCase()}`}>
              <CardHeader className="pb-3"><CardTitle className="text-base"><span className={`px-2 py-1 rounded-full text-xs font-bold ${m.pill}`}><Truck className="w-3 h-3 inline mr-1" />{tt}</span></CardTitle><p className="text-xs text-slate-500 mt-1">{m.tip}</p></CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs flex items-center gap-1"><DollarSign className="w-3 h-3" /> Min $/Mile</Label><Input type="number" step="0.01" value={cfg.min_rpm ?? ''} onChange={e => upd(tt, 'min_rpm', e.target.value)} data-testid={`min-rpm-${tt.replace(/\s+/g,'-').toLowerCase()}`} /></div>
                  <div><Label className="text-xs flex items-center gap-1"><Weight className="w-3 h-3" /> Weight (lb)</Label><Input type="number" value={cfg.weight_lbs ?? ''} onChange={e => upd(tt, 'weight_lbs', e.target.value)} /></div>
                </div>
                <div><Label className="text-xs">Notes</Label><Input value={cfg.notes ?? ''} onChange={e => upd(tt, 'notes', e.target.value)} /></div>
                <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5"><strong>Effect:</strong> Phase 3 AI rejects any load under <span className="font-mono font-bold">${(parseFloat(cfg.min_rpm) || 0).toFixed(2)}/mi</span> for a {tt}.</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Autonomous Rules Editor — controls the AI's global filters (score, rate floor, deadhead, brokers, states) */}
      {autoRules && (
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="autonomous-rules-card">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Bot className="w-5 h-5 text-amber-600" />
                  Autonomous Booking Rules
                  <span className="text-[10px] uppercase tracking-widest text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">Phase 3</span>
                </CardTitle>
                <p className="text-xs text-slate-600 mt-1">Every load the AI evaluates must pass all of these before it's auto-booked.</p>
              </div>
              <Button onClick={saveRules} disabled={savingRules} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-auto-rules-btn">
                {savingRules ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" />Save AI Rules</>}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs flex items-center gap-1"><Shield className="w-3 h-3" /> Minimum AI Score (0–100)</Label>
              <Input
                type="number" min="0" max="100" step="1"
                value={autoRules.min_score ?? ''}
                onChange={e => updRule('min_score', e.target.value)}
                data-testid="rule-min-score"
              />
              <p className="text-[10px] text-slate-500 mt-1">AI won't auto-book below this confidence score.</p>
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><DollarSign className="w-3 h-3" /> Absolute Min $/Mile (global floor)</Label>
              <Input
                type="number" step="0.01"
                value={autoRules.min_rate_per_mile ?? ''}
                onChange={e => updRule('min_rate_per_mile', e.target.value)}
                data-testid="rule-min-rpm"
              />
              <p className="text-[10px] text-slate-500 mt-1">Applied only if a truck type has no override in Fleet Rates above.</p>
            </div>
            <div>
              <Label className="text-xs">Max Deadhead Miles</Label>
              <Input
                type="number" step="1"
                value={autoRules.max_deadhead_miles ?? ''}
                onChange={e => updRule('max_deadhead_miles', e.target.value)}
                data-testid="rule-max-deadhead"
              />
              <p className="text-[10px] text-slate-500 mt-1">Skip loads where the driver has to drive further than this to pickup.</p>
            </div>
            <div>
              <Label className="text-xs">Max Bookings / Day</Label>
              <Input
                type="number" step="1"
                value={autoRules.max_daily_bookings ?? ''}
                onChange={e => updRule('max_daily_bookings', e.target.value)}
                data-testid="rule-max-bookings"
              />
              <p className="text-[10px] text-slate-500 mt-1">Circuit breaker — AI stops after this many auto-books in a 24h window.</p>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Allowed Pickup States (comma-separated, blank = all)</Label>
              <Input
                value={Array.isArray(autoRules.allowed_states) ? autoRules.allowed_states.join(', ') : (autoRules.allowed_states || '')}
                onChange={e => updRule('allowed_states', e.target.value)}
                placeholder="e.g. GA, FL, TX"
                data-testid="rule-allowed-states"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Blocked Brokers (comma-separated substrings)</Label>
              <Input
                value={Array.isArray(autoRules.blocked_brokers) ? autoRules.blocked_brokers.join(', ') : (autoRules.blocked_brokers || '')}
                onChange={e => updRule('blocked_brokers', e.target.value)}
                placeholder="e.g. BadBrokerCo, SlowPay LLC"
                data-testid="rule-blocked-brokers"
              />
              <p className="text-[10px] text-slate-500 mt-1">Case-insensitive substring match on the load's broker name.</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
